/**
 * LabelForm — the input side of the docket-label page. Pulled out of
 * LabelPrinter so the parent can stay slim and the form is independently
 * reviewable. Receives state + setters; owns no fetching.
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, Save, Send } from "lucide-react";
import { ALLERGENS } from "@/lib/allergens";
import { NutritionEditor } from "@/components/NutritionEditor";

export const STORAGE_OPTS = [
  { v: "refrigerated", l: "Refrigerated ≤ 5°C" },
  { v: "frozen", l: "Frozen ≤ -15°C" },
  { v: "hot_hold", l: "Hot hold ≥ 60°C" },
  { v: "ambient", l: "Ambient" },
];

export default function LabelForm({
  form,
  setForm,
  recipes,
  onApplyRecipe,
  onToggleAllergen,
  onSave,
  onPrint,
}) {
  return (
    <div className="lg:col-span-3 surface p-6 md:p-8 space-y-5 no-print">
      <div className="flex items-center gap-2">
        <Printer className="w-5 h-5 text-eucalyptus-700" />
        <h2 className="font-display font-bold text-lg">Label details</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label>Pre-fill from recipe (optional)</Label>
          <Select value={form.recipe_id || undefined} onValueChange={onApplyRecipe}>
            <SelectTrigger data-testid="lb-recipe" className="h-12">
              <SelectValue placeholder="Choose a recipe…" />
            </SelectTrigger>
            <SelectContent>
              {recipes.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Product name</Label>
          <Input data-testid="lb-name" className="h-12" value={form.product_name}
            onChange={(e) => setForm({ ...form, product_name: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label>Batch #</Label>
          <Input data-testid="lb-batch" className="h-12 font-mono" value={form.batch_number}
            onChange={(e) => setForm({ ...form, batch_number: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Quantity / size</Label>
          <Input data-testid="lb-qty" className="h-12" value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Storage</Label>
          <Select value={form.storage} onValueChange={(v) => setForm({ ...form, storage: v })}>
            <SelectTrigger data-testid="lb-storage" className="h-12"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STORAGE_OPTS.map((o) => (
                <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Prep date/time</Label>
          <Input data-testid="lb-prep" type="datetime-local" className="h-12"
            value={form.prep_date} onChange={(e) => setForm({ ...form, prep_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Shelf life (hours)</Label>
          <Input data-testid="lb-shelf" type="number" min="1" className="h-12 w-32"
            value={form.shelf_life_hours}
            onChange={(e) => setForm({ ...form, shelf_life_hours: parseInt(e.target.value || 0, 10) })} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Use-by date/time (auto-calculated)</Label>
          <Input data-testid="lb-useby" type="datetime-local" className="h-12 font-mono"
            value={form.use_by_date}
            onChange={(e) => setForm({ ...form, use_by_date: e.target.value })} />
        </div>
        <div className="md:col-span-2 space-y-2">
          <Label>Allergens (printed on label)</Label>
          <div className="grid grid-cols-2 gap-2 surface-alt p-3">
            {ALLERGENS.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  data-testid={`lb-allergen-${a.id}`}
                  checked={form.allergens.includes(a.id)}
                  onCheckedChange={() => onToggleAllergen(a.id)}
                />
                {a.label}
              </label>
            ))}
          </div>
        </div>
        <div className="md:col-span-2 space-y-2">
          <Label>Notes (optional)</Label>
          <Input data-testid="lb-notes" className="h-12" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        {/* Extra docket elements — used when a custom docket template includes them */}
        <div className="md:col-span-2 pt-2 border-t border-eucalyptus-700/15">
          <p className="label-eyebrow">Extra docket details</p>
          <p className="text-xs text-charcoal-soft mt-0.5">Fill any of these if your custom docket template uses them.</p>
        </div>
        <div className="space-y-2">
          <Label>Price (AUD)</Label>
          <Input data-testid="lb-price" type="number" step="0.01" min="0" className="h-12" value={form.price_aud}
            onChange={(e) => setForm({ ...form, price_aud: e.target.value })} placeholder="8.50" />
        </div>
        <div className="space-y-2">
          <Label>Best before</Label>
          <Input data-testid="lb-bestbefore" type="date" className="h-12" value={form.best_before}
            onChange={(e) => setForm({ ...form, best_before: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Staff ID</Label>
          <Input data-testid="lb-staff" className="h-12" value={form.staff_id}
            onChange={(e) => setForm({ ...form, staff_id: e.target.value })} placeholder="e.g. J. Smith" />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Ingredients</Label>
          <textarea data-testid="lb-ingredients" rows={2}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={form.ingredients}
            onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
            placeholder="Beef, wheat flour, milk, egg…" />
        </div>
        <div className="md:col-span-2">
          <NutritionEditor value={form.nutrition} onChange={(n) => setForm({ ...form, nutrition: n })} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-end items-center pt-2">
        <div className="flex items-center gap-2 mr-auto" data-testid="lb-copies">
          <Label className="text-sm">Copies</Label>
          <div className="flex items-center gap-1">
            <button type="button" data-testid="lb-copies-minus"
              onClick={() => setForm({ ...form, copies: Math.max(1, (form.copies || 1) - 1) })}
              disabled={(form.copies || 1) <= 1}
              className="w-9 h-9 grid place-items-center rounded border border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100 disabled:opacity-30"
              aria-label="Fewer copies">−</button>
            <span data-testid="lb-copies-val" className="w-10 text-center font-mono font-semibold tabular-nums">{form.copies || 1}×</span>
            <button type="button" data-testid="lb-copies-plus"
              onClick={() => setForm({ ...form, copies: Math.min(99, (form.copies || 1) + 1) })}
              disabled={(form.copies || 1) >= 99}
              className="w-9 h-9 grid place-items-center rounded border border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100 disabled:opacity-30"
              aria-label="More copies">+</button>
          </div>
        </div>
        <Button
          onClick={onSave}
          data-testid="lb-save"
          variant="outline"
          className="h-12 gap-2 border-eucalyptus-700 text-eucalyptus-900"
        >
          <Save className="w-4 h-4" /> Save
        </Button>
        <Button
          onClick={onPrint}
          data-testid="lb-print"
          className="h-12 gap-2 bg-ochre-600 hover:bg-ochre-400 text-white"
        >
          <Send className="w-4 h-4" /> Batch print
        </Button>
      </div>
    </div>
  );
}
